/**
 * RegisterWardModal
 *
 * Permite a un ACUDIENTE (padre/madre/tutor) registrar a una persona a su cargo
 * (su hijo) en un club, desde su propia cuenta. Crea un `joinRequest` con
 * `forWard=true` y los datos del HIJO; al aprobarlo el director, la persona se
 * crea como dependiente (`guardianUserId` = esta cuenta), no como cuenta propia.
 *
 * Pensado para el campamento de Aventureros: los padres registran a sus hijos.
 */
import { useEffect, useMemo, useState } from "react";
import { Button, Input, Select, Modal, ModalFooter } from "@/components/ui";
import { toast } from "react-hot-toast";
import { useAuthStore } from "@/stores/authStore";
import { useClubs } from "@/hooks/useOrganization";
import * as joinRequestService from "@/lib/services/joinRequests";
import { personService } from "@/lib/services/persons";
import {
  getActiveSections,
  isImplausibleBirthDate,
  calculateAge,
  resolveSectionForClub,
} from "@/lib/utils";
import type { Club, Person } from "@/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function RegisterWardModal({
  isOpen,
  onClose,
  onSuccess,
}: Props) {
  const { user, profile } = useAuthStore();
  const { data: clubs } = useClubs();
  const activeClubs = useMemo(
    () => (clubs ?? []).filter((c) => c.status !== "inactive"),
    [clubs],
  );

  // Carga la Person del acudiente para pre-llenar contacto/dirección.
  const [guardianPerson, setGuardianPerson] = useState<Person | null>(null);
  useEffect(() => {
    if (!isOpen || !user?.uid) return;
    let cancelled = false;
    personService
      .getByLinkedUser(user.uid)
      .then((p) => {
        if (!cancelled) setGuardianPerson(p);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, user?.uid]);

  const [clubId, setClubId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [idDocumentType, setIdDocumentType] = useState("RC");
  const [idDocumentNumber, setIdDocumentNumber] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState("");
  const [bloodType, setBloodType] = useState("O+");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [healthInsurance, setHealthInsurance] = useState("");
  const [relation, setRelation] = useState("madre");
  const [useGuardianContact, setUseGuardianContact] = useState(true);
  const [saving, setSaving] = useState(false);

  // Autofill inicial con datos del acudiente (persona/perfil).
  useEffect(() => {
    if (!useGuardianContact) return;
    const gPhone = guardianPerson?.phone ?? profile?.phone ?? "";
    const gAddress = guardianPerson?.address ?? "";
    const gEps = guardianPerson?.healthInsurance ?? "";
    if (!phone) setPhone(gPhone);
    if (!address) setAddress(gAddress);
    if (!healthInsurance) setHealthInsurance(gEps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardianPerson, profile?.phone, useGuardianContact]);

  const age = birthDate ? calculateAge(birthDate) : null;

  const validate = (): string | null => {
    if (!clubId) return "Selecciona el club.";
    if (firstName.trim().length < 2) return "Ingresa los nombres del menor.";
    if (lastName.trim().length < 2) return "Ingresa los apellidos del menor.";
    if (idDocumentNumber.trim().length < 4)
      return "Ingresa el número de documento.";
    if (!birthDate) return "Ingresa la fecha de nacimiento.";
    if (isImplausibleBirthDate(birthDate))
      return "Revisa la fecha de nacimiento (no puede ser futura ni dar edad 0).";
    if (!gender) return "Selecciona el sexo.";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    if (!user?.uid) return;
    const club = activeClubs.find((c) => c.id === clubId) as Club | undefined;
    if (!club) {
      toast.error("Club no encontrado.");
      return;
    }
    setSaving(true);
    try {
      const derivedSection =
        (age != null
          ? resolveSectionForClub(new Date(birthDate + "T12:00:00"), club)
          : null) ??
        getActiveSections(club)[0] ??
        club.type ??
        "aventureros";
      const guardianName = profile?.displayName ?? user.displayName ?? "";
      const childFullName = `${firstName.trim()} ${lastName.trim()}`.trim();

      await joinRequestService.createJoinRequest({
        userId: user.uid,
        userEmail: user.email ?? "",
        userName: childFullName, // datos personales = el HIJO
        userPhotoUrl: "",
        clubId: club.id,
        clubName: club.name,
        clubType: String(derivedSection),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        birthDate: new Date(birthDate + "T12:00:00"),
        idDocumentType,
        idDocumentNumber: idDocumentNumber.trim(),
        gender,
        bloodType,
        address: address.trim(),
        healthInsurance: healthInsurance.trim(),
        memberType: "acampante",
        emergencyContact: {
          name: guardianPerson?.emergencyContact?.name ?? guardianName ?? "",
          phone:
            guardianPerson?.emergencyContact?.phone ??
            guardianPerson?.phone ??
            profile?.phone ??
            "",
          relationship:
            guardianPerson?.emergencyContact?.relationship ?? relation,
        },
        forWard: true,
        guardianName,
        guardianRelation: relation,
        status: "pending",
      });

      toast.success(
        `Solicitud enviada para ${childFullName}. El director del club la aprobará.`,
      );
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "No se pudo enviar la solicitud",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Registrar a una persona a mi cargo"
      description="Registra a tu hijo/a en un club. Quedará a tu cargo y podrás gestionar su check-in y documentos. El director del club aprueba la solicitud."
      size="lg"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Club al que se unirá <span className="text-red-500">*</span>
          </label>
          <Select value={clubId} onChange={(e) => setClubId(e.target.value)}>
            <option value="">Seleccionar club…</option>
            {activeClubs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <p className="mt-1 text-xs text-gray-500 leading-relaxed">
            Se enviará una solicitud al director del club que elijas. Él la
            revisará y aprobará el registro de tu hijo/a. La sección
            (Aventureros, Conquistadores o Guías Mayores) se asigna
            automáticamente según la edad.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombres *
          </label>
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Apellidos *
          </label>
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de documento *
          </label>
          <Select
            value={idDocumentType}
            onChange={(e) => setIdDocumentType(e.target.value)}
          >
            <option value="RC">Registro Civil (RC)</option>
            <option value="TI">Tarjeta de Identidad (TI)</option>
            <option value="CC">Cédula (CC)</option>
            <option value="CE">Cédula de Extranjería (CE)</option>
            <option value="PASAPORTE">Pasaporte</option>
            <option value="PPT">Permiso por Protección Temporal (PPT)</option>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Número de documento *
          </label>
          <Input
            value={idDocumentNumber}
            onChange={(e) => setIdDocumentNumber(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha de nacimiento *
          </label>
          <Input
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
          />
          {age != null && (
            <p className="text-xs text-gray-500 mt-1">{age} años</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sexo *
          </label>
          <Select value={gender} onChange={(e) => setGender(e.target.value)}>
            <option value="">Seleccionar…</option>
            <option value="male">Masculino</option>
            <option value="female">Femenino</option>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de sangre
          </label>
          <Select
            value={bloodType}
            onChange={(e) => setBloodType(e.target.value)}
          >
            {["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"].map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            EPS (opcional)
          </label>
          <Input
            value={healthInsurance}
            onChange={(e) => setHealthInsurance(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Teléfono {useGuardianContact ? "(tomamos el tuyo)" : "(opcional)"}
          </label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tu relación con el menor *
          </label>
          <Select
            value={relation}
            onChange={(e) => setRelation(e.target.value)}
          >
            <option value="madre">Madre</option>
            <option value="padre">Padre</option>
            <option value="tutor">Tutor</option>
            <option value="abuelo">Abuelo</option>
            <option value="abuela">Abuela</option>
            <option value="otro">Otro</option>
          </Select>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dirección {useGuardianContact && "(tomamos la tuya)"}
          </label>
          <Input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Calle, ciudad"
          />
        </div>

        <div className="sm:col-span-2 -mt-1">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              className="rounded"
              checked={useGuardianContact}
              onChange={(e) => {
                setUseGuardianContact(e.target.checked);
                if (!e.target.checked) {
                  setPhone("");
                  setAddress("");
                }
              }}
            />
            <span>
              Usar mi teléfono, dirección y contacto de emergencia para{" "}
              {firstName || "este menor"}
            </span>
          </label>
          {useGuardianContact && guardianPerson?.emergencyContact?.name && (
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Contacto de emergencia:{" "}
              <span className="font-medium">
                {guardianPerson.emergencyContact.name}
              </span>{" "}
              ({guardianPerson.emergencyContact.relationship})
            </p>
          )}
        </div>
      </div>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? "Enviando…" : "Enviar solicitud"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
